import { camelCase } from 'lodash';
import PropTypes from 'prop-types';

import useFetchDisabledPatientFields from 'App/hooks/useFetchDisabledPatientFields';
import useFetchRequiredPatientFields from 'App/hooks/useFetchRequiredPatientFields';
import withFormikFields from 'Shared/form/withFormikFields';

const alwaysRequiredFields = [
    'firstName',
    'lastName',
    'birthDate',
    'gender',
    'city',
    'state',
    'zip',
    'planName',
    'patientId',
    'benefitCoverageType',
    'payorType',
];

const isFieldInObject = (fieldName, object) => {
    return !!Object.values(object).find((field) => camelCase(field.field) === fieldName);
};

const hasRequiredField = (fieldNames, requiredFields = {}) => {
    return fieldNames.some(
        (fieldName) => alwaysRequiredFields.includes(fieldName) || isFieldInObject(fieldName, requiredFields)
    );
};

const useHasRequiredField = () => {
    const { data: requiredFields = {} } = useFetchRequiredPatientFields();
    return (fieldNames) => hasRequiredField(fieldNames, requiredFields);
};

const CommonPatientFieldWrapper = ({ fieldName, children, fieldValues: { requiredOnly, disableInputs } }) => {
    const { data: disabledFields = {} } = useFetchDisabledPatientFields();
    const hasRequired = useHasRequiredField();

    const disabled = isFieldInObject(fieldName, disabledFields) || disableInputs;
    const required = hasRequired([fieldName]);

    if (requiredOnly && !required) {
        return null;
    }

    const commonProps = {
        fieldName,
        disabled,
        required,
    };
    return children(commonProps);
};

CommonPatientFieldWrapper.propTypes = {
    fieldName: PropTypes.string.isRequired,
    children: PropTypes.func.isRequired,
    fieldValues: PropTypes.shape({
        requiredOnly: PropTypes.bool,
        disableInputs: PropTypes.bool,
    }).isRequired,
};

const WrapperWithFields = withFormikFields({
    WrappedComponent: CommonPatientFieldWrapper,
    fieldNames: ['requiredOnly', 'disableInputs'],
});

export { useHasRequiredField, WrapperWithFields as default };
